import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle, Share2, BarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import NavBar from "@/components/woost/NavBar";
import Footer from "@/components/woost/Footer";
import ScrollToTop from "@/components/woost/ScrollToTop";
import {
  caseStudies,
  CaseStudyType,
} from "@/components/woost/CaseStudiesSection";

const CaseStudyDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [caseStudy, setCaseStudy] = useState<CaseStudyType | null>(null);

  useEffect(() => {
    // Find the case study based on the slug
    const foundCaseStudy = caseStudies.find((cs) => cs.slug === slug);

    if (foundCaseStudy) {
      setCaseStudy(foundCaseStudy);
      window.scrollTo(0, 0);
    }
  }, [slug]);

  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Case Study Not Found</h1>
          <Link to="/">
            <Button>Return to Homepage</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = caseStudy.icon;

  const getCaseStudyContent = (id: string) => {
    switch (id) {
      case "1":
        return {
          performance: "1.1M reach, 20% increase in website visits",
          engagement: "5K+ comments, likes, and shares",
          reach: "20 creators with a combined 750K followers",
          Challenge:
            "Bombay Shaving Company, a leading men’s grooming brand known for its high-quality personal care products, aimed to create buzz and drive engagement for a new product launch. Targeting modern, style-conscious men, the brand was looking for a cost-effective strategy to amplify awareness and generate authentic conversations, without investing in high-budget media.",
          Strategy:
            "Woost collaborated with Bombay Shaving Company to launch a barter-based influencer campaign. The initiative brought together 20 macro and select nano influencers from the skincare, grooming, and lifestyle categories, each chosen to closely align with the brand’s audience. In exchange for product samples, these creators delivered honest, experience-based content that focused on real benefits and everyday use.",
          CampaigninAction:
            "Over 4 weeks, influencers created and shared posts across Instagram, featuring the product in grooming routines, morning rituals, and travel essentials. Woost ensured seamless campaign coordination, from influencer briefing to content review. The content emphasized real-life application, ease of use, and visible results, helping to build relatability and trust with their followers.",
          Results:
            "The campaign had a reach of over 1.1 million, significantly boosting brand visibility. It saw a surge in engagement, driven by likes, shares, and authentic creator interactions. Website traffic grew substantially, with much of it attributed to influencer-led content. Most importantly, sales rose by double digits during the campaign, validating the effectiveness of community-led marketing with Woost’s support.",
        };
      case "2":
        return {
          performance: "125% increase in sales during the campaign",
          engagement: "Authentic content from 30+ conscious creators",
          reach: "Significant lift in marketplace discovery and brand trust",
          Challenge:
            "Indeekos, a D2C skincare brand rooted in sustainability and scientific innovation, aimed to strengthen its presence across competitive digital marketplaces. While the brand offered clean, effective skincare solutions, it faced challenges in standing out and building immediate trust in a crowded online space. The key objective was to increase brand visibility and credibility in an authentic, cost-efficient way.",
          Strategy:
            "To solve this, Indeekos partnered with Woost to implement an influencer-powered campaign designed to drive real user engagement. Woost identified a cohort of conscious creators who aligned with Indeekos’ brand values and skincare philosophy. These individuals were given the brand’s products to use in their daily routines, to inspire honest conversations and relatable content that would organically boost brand presence and marketplace performance.",
          CampaigninAction:
            "With support from Woost, participants experienced the products firsthand and documented their journeys across digital channels, focusing on aspects like texture, results, packaging, and ingredient transparency. The campaign was structured to feel natural and user-led, with Woost managing participant selection, product distribution, content coordination, and reporting.",
          Results:
            "The campaign led to a meaningful improvement in Indeekos’ online visibility and customer trust. Authentic stories from real users helped build social proof and drove increased discovery across marketplaces. Backed by Woost’s strategic execution and community insights, Indeekos successfully enhanced its digital footprint, translating authentic content into measurable gains in brand performance.",
        };
      case "3":
        return {
          performance: "Improved website engagement and user retention.",
          engagement:
            "High interaction on creator videos—comments, shares, and saves.",
          reach: "Wide exposure across Reels, Shorts via authentic UGC.",
          Challenge:
            "A growing D2C company wanted to scale brand visibility and deepen customer engagement without relying on high-budget ad campaigns. With an increasingly competitive landscape and ad fatigue among consumers, the brand sought a more authentic and cost-effective content strategy.",
          Strategy:
            "The Woost team launched a UGC-driven campaign focused on real users creating short-form video content. Selected participants were encouraged to showcase how they use the product in their daily routines, share honest thoughts, or highlight benefits that matter to them. These videos were optimized for platforms like Instagram Reels and YouTube Shorts to drive reach and relatability.",
          CampaigninAction:
            "Content creators were onboarded with clear creative direction while allowing flexibility to keep their voice and style intact. The videos ranged from quick testimonials and how-tos to lifestyle integrations and product highlights. The brand repurposed the best-performing UGC across digital touchpoints, including social media, product pages, and email campaigns.",
          Results:
            "The campaign generated a wide range of diverse, relatable content that boosted organic engagement and significantly improved brand storytelling. It also helped the brand build trust, tap into new audiences, and drive conversions without the overhead of traditional ad production.",
        };
      default:
        return {
          performance:
            "Drove 12,000+ product page visits and 1,300+ orders during the campaign window.",
          engagement:
            "Generated 8,200+ likes, 1,100+ comments, and 150+ shares across.",
          reach:
            "Achieved over 1.8 million+ impressions through creator content.",
          Challenge:
            "Agaro, a premium D2C brand known for its high-performance home and lifestyle appliances, wanted to expand its digital footprint and build trust with modern, value-conscious consumers. Despite a strong product portfolio and growing market presence, the brand needed a more impactful and cost-effective strategy to reach new audiences and increase conversions in a competitive online landscape.",
          Strategy:
            "Agaro teamed up with Woost to launch a performance-focused paid influencer campaign. Leveraging Woost’s platform, the brand identified and activated a handpicked network of micro and macro creators in the tech, home, and lifestyle categories. These influencers were selected based on audience relevance, content quality, and past performance. Woost handled everything from creator onboarding to content coordination, allowing Agaro to stay focused on brand messaging and product positioning.",
          CampaigninAction:
            "Influencers received Agaro’s flagship products along with campaign briefs to craft real-life use content. From demo Reels and setup videos to unboxings and how-tos, creators shared product experiences in a relatable, informative way. Discount codes and trackable links were integrated to monitor conversions and ROI.",
          Results:
            "The campaign led to a notable increase in online visibility and customer engagement. Influencer content drove qualified traffic to Agaro’s website and enhanced product discoverability across platforms. Most importantly, the brand experienced an uplift in conversions, with consumers citing influencer recommendations as a key driver of trust. With Woost’s end-to-end support—from influencer curation to analytics—Agaro efficiently scaled its D2C growth through authentic, data-driven content.",
        };
    }
  };

  const caseStudyContent = getCaseStudyContent(caseStudy.id);

  return (
    <div className="min-h-screen bg-white">
      <NavBar />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px]">
          <div className="absolute inset-0">
            <img
              src={caseStudy.image_detail}
              alt={caseStudy.title}
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/30"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6 relative h-full z-10">
            <div className="flex flex-col h-full justify-center max-w-3xl">
              <Link to="/#case-studies" className="mb-6">
                <Button
                  variant="outline"
                  className="bg-white/20 border-white/40 text-white hover:bg-white/30"
                >
                  <ArrowLeft size={16} className="mr-2" /> Back to Case Studies
                </Button>
              </Link>
              <div className={`${caseStudy.color} p-2 rounded-lg w-fit mb-4`}>
                <Icon size={24} className="text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {caseStudy.title}
              </h1>
              <p className="text-white/90 text-xl">{caseStudy.description}</p>
            </div>
          </div>
        </section>

        {/* Results Overview */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 -mt-20 relative z-20">
              <h2 className="text-2xl font-bold mb-6">Key Results</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
                  <BarChart
                    size={40}
                    className={`${caseStudy.color} text-white p-2 rounded-full mb-3`}
                  />
                  <h3 className="text-xl font-semibold mb-2">Performance</h3>
                  <p className="text-gray-600">
                    {caseStudyContent.performance}
                  </p>
                </div>

                <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
                  <CheckCircle
                    size={40}
                    className={`${caseStudy.color} text-white p-2 rounded-full mb-3`}
                  />
                  <h3 className="text-xl font-semibold mb-2">Engagement</h3>
                  <p className="text-gray-600">{caseStudyContent.engagement}</p>
                </div>

                <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-lg">
                  <Share2
                    size={40}
                    className={`${caseStudy.color} text-white p-2 rounded-full mb-3`}
                  />
                  <h3 className="text-xl font-semibold mb-2">Reach</h3>
                  <p className="text-gray-600">{caseStudyContent.reach}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Study Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg grid gap-8">
                <div>
                  <h2 className="font-extrabold text-lg mb-2">Challenge</h2>
                  <p>{caseStudyContent.Challenge}</p>
                </div>

                <div>
                  <h2 className="font-extrabold text-lg mb-2">Strategy</h2>
                  <p>{caseStudyContent.Strategy}</p>
                </div>

                <div>
                  <h2 className="font-extrabold text-lg mb-2">
                    Campaign in Action
                  </h2>
                  <p>{caseStudyContent.CampaigninAction}</p>
                </div>

                <div>
                  <h2 className="font-extrabold text-lg mb-2">Results</h2>
                  <p>{caseStudyContent.Results}</p>
                </div>
              </div>

              <div className="mt-12 border-t border-gray-200 pt-8">
                <h3 className="text-xl font-bold mb-4">
                  Ready to achieve similar results?
                </h3>
                <p className="text-gray-600 mb-6">
                  Let's discuss how Woost can help your brand connect with the
                  right creators and drive measurable results.
                </p>
                <a href="/#contact"  scroll-to-id="contact">
                  <Button className="bg-woost-blue hover:bg-woost-blue-dark text-white">
                    Get Started Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default CaseStudyDetail;
